import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ArrowLeft, Mail, Phone, MapPin, User } from 'lucide-react';
import { supabase } from '../../services/supabase';

const QuoteDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuoteDetails();
  }, [id]);

  const fetchQuoteDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select(`
          *,
          quote_contacts (*),
          quote_estimates (*),
          quote_clarifying_questions (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setQuote(data);
    } catch (error) {
      console.error('Error fetching quote details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h2 className="text-2xl font-semibold mb-4">Quote not found</h2>
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </button>
      </div>
    );
  }

  const contact = quote.quote_contacts[0] || {};
  const estimate = quote.quote_estimates[0] || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Quote Request Details</h2>
            <p className="text-sm text-gray-500">
              Submitted on {format(new Date(quote.created_at), 'MMMM d, yyyy')}
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Customer Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <User className="h-5 w-5 text-gray-400 mt-1 mr-2" />
                    <div>
                      <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-gray-400 mt-1 mr-2" />
                    <div>
                      <p>{contact.email}</p>
                      <p className="text-sm text-gray-500">
                        Preferred contact: {contact.preferred_contact}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-gray-400 mt-1 mr-2" />
                    <p>{contact.phone}</p>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-400 mt-1 mr-2" />
                    <div>
                      <p>{contact.address}</p>
                      <p>{contact.city}, {contact.zip_code}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">Description</p>
                    <p className="text-gray-600">{quote.description}</p>
                  </div>
                  <div>
                    <p className="font-medium">Timeframe</p>
                    <p className="text-gray-600">{quote.timeframe}</p>
                  </div>
                  <div>
                    <p className="font-medium">Estimated Price Range</p>
                    <p className="text-gray-600">
                      ${estimate.min_price} - ${estimate.max_price}
                    </p>
                    <p className="text-sm text-gray-500 capitalize">
                      {estimate.confidence} confidence
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {quote.quote_clarifying_questions?.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-4">Clarifying Questions</h3>
                <div className="space-y-4">
                  {quote.quote_clarifying_questions.map((q, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium">{q.question}</p>
                      <p className="text-gray-600 mt-1">Answer: {q.answer || 'Not answered'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetails;